import { useMemo } from 'react';
import { SharedValue, useSharedValue, useWorkletCallback } from 'react-native-reanimated';

import {
    ScrollEventHandlerCallbackType,
    ScrollEventsHandlersHookType,
    useScrollEventsHandlersDefault,
} from '@gorhom/bottom-sheet';
import { ScrollEventContextType } from '@gorhom/bottom-sheet/lib/typescript/hooks/useScrollEventsHandlersDefault';

/**
 * Custom hook for managing and capturing scroll events on bottom sheet scrollable elements.
 *
 * Bottom sheet allows us to send a custom hook to be used for scroll events
 *
 * This hook provides:
 * - A custom scroll event handler hook (`useDefaultHook`) that extends the default scroll event behavior.
 *   It updates a shared vertical scroll position (`scrollY`) whenever a scroll occurs, while still allowing
 *   the original scroll event callback to be executed to ensure the default behavior is preserved
 *   This hook should be used as the `scrollEventsHandlersHook` prop in the `BottomSheetFlatList` component
 *
 * - A shared value (`scrollY`) that represents the current vertical scroll offset. This value can be used
 *   to drive animations or other UI updates based on the scroll position (e.g. animating a header)
 *
 * link to issue with some examples: https://github.com/gorhom/react-native-bottom-sheet/issues/630
 *
 */
export const useScrollEventsHandlersBottomSheet: () => {
    useDefaultHook: ScrollEventsHandlersHookType;
    scrollY: SharedValue<number>;
} = () => {
    const scrollY = useSharedValue(0);

    return useMemo(
        () => ({
            useDefaultHook: (scrollableRef, scrollableContentOffsetY) => {
                const { handleOnScroll, ...rest } = useScrollEventsHandlersDefault(
                    scrollableRef,
                    scrollableContentOffsetY
                );

                const handleOnScrollCustom: ScrollEventHandlerCallbackType<ScrollEventContextType> = useWorkletCallback(
                    (event, ctx) => {
                        scrollY.value = event.contentOffset.y;
                        handleOnScroll?.(event, ctx as never);
                    },
                    [handleOnScroll, scrollY]
                );

                return {
                    ...rest,
                    handleOnScroll: handleOnScrollCustom,
                };
            },
            scrollY,
        }),
        [scrollY]
    );
};
